/**
 * Google reCAPTCHA v3 for lead forms.
 *
 * Invisible and score-based: visitors never see a challenge. The site key is
 * public by design (it is served to every browser), so it lives here; the
 * secret key belongs only in the backend environment as RECAPTCHA_SECRET_KEY
 * and must never be committed.
 *
 * Tokens are attached by the request interceptor in lib/api.js, so every
 * POST to /api/leads/* is covered without each form wiring it up. If the
 * script cannot load (blocked, offline, wrong domain) the request goes out
 * without a token and the backend decides, which keeps a third-party failure
 * from silently breaking a form.
 */
export const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Lfn0fstAAAAAACsKxgrwrFpO_bWDIFsyUnr0WAk';

const LOAD_TIMEOUT_MS = 5000;
const EXECUTE_TIMEOUT_MS = 5000;

let loading = null;

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

export function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined') return Promise.resolve(null);
  if (window.grecaptcha && window.grecaptcha.execute) {
    return new Promise((resolve) => window.grecaptcha.ready(() => resolve(window.grecaptcha)));
  }
  if (!loading) {
    loading = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;
      script.async = true;
      script.onload = () => window.grecaptcha.ready(() => resolve(window.grecaptcha));
      script.onerror = () => {
        // Allow a later attempt instead of caching the failure for the session.
        loading = null;
        script.remove();
        resolve(null);
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

/** A fresh token for `action`, or null. Tokens are single-use and expire after two minutes. */
export async function getRecaptchaToken(action) {
  try {
    const grecaptcha = await withTimeout(loadRecaptcha(), LOAD_TIMEOUT_MS);
    if (!grecaptcha) return null;
    const token = await withTimeout(grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }), EXECUTE_TIMEOUT_MS);
    return typeof token === 'string' && token ? token : null;
  } catch (e) {
    return null;
  }
}

/**
 * The action name for a lead endpoint: '/leads/homeowner-quote' -> 'homeowner_quote'.
 * The backend checks the verified action against the endpoint it arrived at.
 */
export function leadAction(url) {
  const match = /\/leads\/([a-z0-9-]+)/i.exec(String(url || ''));
  return match ? match[1].toLowerCase().replace(/-/g, '_') : null;
}
